/**
 * @file navStore.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-05-14
 * @modified 2026-05-14
 *
 * Cross-navigation page-state store.
 *
 * Each page (or component) writes a slice keyed by a string id, and reads it
 * back on remount. The store is persisted to sessionStorage so it survives
 * tab-local navigation but not a full reload of a new tab — that matches the
 * mental model of "what was I doing in this tab".
 *
 * The hook for component-level usage is usePersistedState in
 * hooks/usePersistedState.js — components shouldn't import this store
 * directly except for housekeeping (clearAllPages on logout).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export const useNavStore = create(
  persist(
    (set, get) => ({
      pages: {},

      getPage: (key) => get().pages[key],

      setPage: (key, value) =>
        set((state) => ({
          pages: { ...state.pages, [key]: value },
        })),

      clearPage: (key) =>
        set((state) => {
          if (!(key in state.pages)) return state;
          const { [key]: _drop, ...rest } = state.pages;
          return { pages: rest };
        }),

      clearAllPages: () => set({ pages: {} }),
    }),
    {
      name: 'excel-agri-nav',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist the pages object — actions are recreated on every load.
      partialize: (state) => ({ pages: state.pages }),
    }
  )
);
